import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  Clock,
  X,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { ReconcileConfirmDialog } from './ReconcileConfirmDialog';
import { ReconcileRunHistory } from './ReconcileRunHistory';
import {
  useReconcileRun,
  useReconcileRuns,
  asReconcileScope,
  asReconcileStats,
  statNum,
  isActiveStatus,
  VARIANT_LINKS_ARCHIVE_THRESHOLD,
} from '../hooks/useReconcile';

type ReconcileMode = 'full' | 'targeted';

const PREFIX_PATTERN = /^[A-Z0-9-]+$/;

/** A single count chip in the live-status / result rows. */
function CountChip({
  label,
  value,
  amber,
  tone = 'default',
  showZero = false,
}: {
  label: string;
  value: number;
  amber?: boolean;
  tone?: 'default' | 'destructive';
  /**
   * Render even when the value is 0. On a "Reconcile complete" summary, zero is
   * meaningful ("did it do anything?"); on the noisy LIVE poll it's hidden so the
   * row doesn't flicker with empty chips.
   */
  showZero?: boolean;
}) {
  if (value <= 0 && !showZero) return null;
  return (
    <Badge
      variant={tone === 'destructive' ? 'destructive' : 'secondary'}
      className={cn(
        'text-xs tabular-nums',
        amber && 'bg-amber-100 text-amber-800 hover:bg-amber-100',
      )}
    >
      {value.toLocaleString()} {label}
    </Badge>
  );
}

export function ReconcilePanel() {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();

  const [mode, setMode] = useState<ReconcileMode>('full');
  const [prefix, setPrefix] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  // Poll the active run (server-state only — no local isRunning mirror).
  const runQuery = useReconcileRun(activeRunId);
  const run = runQuery.data ?? null;

  // Latest run from history — drives the persistent card-level warning when a
  // completed run archived a large number of links or tripped the breaker.
  const runsQuery = useReconcileRuns(20);
  const latestRun = runsQuery.data?.[0] ?? null;
  const latestStats = useMemo(() => asReconcileStats(latestRun?.stats), [latestRun?.stats]);
  const latestArchived = statNum(latestStats, 'linksArchived');
  const latestBreaker = latestStats.circuitBreaker === true;
  const showCardWarning =
    !!latestRun &&
    (latestRun.status === 'completed' || latestRun.status === 'aborted_safeguard') &&
    (latestBreaker || latestArchived >= VARIANT_LINKS_ARCHIVE_THRESHOLD) &&
    // Suppress when the same run is already surfaced inline above (live result
    // block). The card-warning is a persistent post-navigation reminder, not a
    // co-resident of the live result — otherwise the same run shows two amber boxes.
    (!run || run.id !== latestRun.id);
  const runStats = useMemo(() => asReconcileStats(run?.stats), [run?.stats]);
  const runScope = useMemo(() => asReconcileScope(run?.scope), [run?.scope]);

  const running = run ? isActiveStatus(run.status) : false;

  // Once a tracked run reaches a terminal state, refresh the history list and
  // toast its outcome exactly once.
  const settledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run || isActiveStatus(run.status)) return;
    if (settledRef.current === run.id) return;
    settledRef.current = run.id;

    void utils.stockSync.listReconcileRuns.invalidate();

    const corrected = statNum(runStats, 'corrected');
    const upserted = statNum(runStats, 'linksUpserted');
    const archived = statNum(runStats, 'linksArchived');

    if (run.status === 'completed') {
      toast.success(
        t(
          'stockSync.reconcile.toast.complete',
          'Reconcile complete. {{corrected}} corrected, {{upserted}} links added, {{archived}} archived.',
          { corrected, upserted, archived },
        ),
      );
    } else if (run.status === 'aborted_safeguard') {
      toast.warning(
        t('stockSync.reconcile.toast.aborted', 'Reconcile paused for safety. No Shopify stock was changed.'),
        { duration: Infinity },
      );
    } else if (run.status === 'failed') {
      toast.error(
        t('stockSync.reconcile.toast.failed', 'Reconcile failed: {{error}}', {
          error: run.error ?? t('stockSync.reconcile.toast.unknownError', 'unknown error'),
        }),
        { duration: Infinity },
      );
    }
  }, [run, runStats, t, utils]);

  const triggerReconcile = trpc.stockSync.triggerReconcile.useMutation({
    onSuccess: (res) => {
      settledRef.current = null;
      setActiveRunId(res.runId);
      void utils.stockSync.listReconcileRuns.invalidate();
    },
    onError: (error) => {
      // Surface the backend's typed message (CONFLICT / PRECONDITION_FAILED).
      toast.error(error.message, { duration: Infinity });
    },
  });

  // --- Targeted prefix validation (live, before submit) ---
  const trimmedPrefix = prefix.trim().toUpperCase();
  const prefixError =
    mode === 'targeted' && prefix.length > 0 && !PREFIX_PATTERN.test(trimmedPrefix)
      ? t('stockSync.reconcile.prefixInvalid', 'Use uppercase letters, digits, and dashes only.')
      : mode === 'targeted' && prefix.length > 0 && trimmedPrefix.length < 2
        ? t('stockSync.reconcile.prefixTooShort', 'Enter at least 2 characters.')
        : null;
  const targetedReady =
    mode === 'targeted' && trimmedPrefix.length >= 2 && PREFIX_PATTERN.test(trimmedPrefix);

  const submitDisabled =
    triggerReconcile.isPending || running || (mode === 'targeted' && !targetedReady);

  const handlePrimary = () => {
    if (mode === 'full') {
      setShowConfirm(true);
    } else {
      triggerReconcile.mutate({ scope: { skuPrefix: trimmedPrefix } });
    }
  };

  const handleConfirmFull = () => {
    triggerReconcile.mutate(
      { scope: null },
      { onSettled: () => setShowConfirm(false) },
    );
  };

  // --- Live-status derived flags ---
  const checked = statNum(runStats, 'variantsChecked');
  const hasDenominator = checked > 0;
  const archivedLive = statNum(runStats, 'linksArchived');
  const linksEmpty = runStats.linksEmpty === true;
  const isAborted = run?.status === 'aborted_safeguard';
  const startedAt = run?.started_at ?? run?.created_at ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
          {t('stockSync.reconcile.title', 'Reconcile & force-push now')}
        </CardTitle>
        <CardDescription>
          {t(
            'stockSync.reconcile.description',
            'Force-corrects Shopify stock for SKUs that have drifted from PrintRun. Writes directly to your live Shopify store.',
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Mode toggle + targeted input — reserved height so toggling never shifts layout. */}
        <div className="space-y-3">
          <Tabs value={mode} onValueChange={(v) => setMode(v as ReconcileMode)}>
            <TabsList>
              <TabsTrigger
                value="full"
                className="data-[state=active]:bg-threadheads-purple data-[state=active]:text-white"
                disabled={running}
              >
                {t('stockSync.reconcile.modeFull', 'Full')}
              </TabsTrigger>
              <TabsTrigger
                value="targeted"
                className="data-[state=active]:bg-threadheads-purple data-[state=active]:text-white"
                disabled={running}
              >
                {t('stockSync.reconcile.modeTargeted', 'Targeted')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Fixed-height slot — present in both modes so there is no layout shift. */}
          <div className="min-h-[4.75rem]">
            {mode === 'targeted' ? (
              <div className="space-y-1.5">
                <Label htmlFor="reconcile-prefix" className="text-sm">
                  {t('stockSync.reconcile.prefixLabel', 'SKU or prefix')}
                </Label>
                <Input
                  id="reconcile-prefix"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && targetedReady && !submitDisabled) handlePrimary();
                  }}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder={t('stockSync.reconcile.prefixPlaceholder', 'HT-L-BK')}
                  disabled={running}
                  aria-invalid={!!prefixError}
                  aria-describedby={prefixError ? 'reconcile-prefix-error' : undefined}
                  className="max-w-prose font-mono uppercase"
                />
                {prefixError ? (
                  <p id="reconcile-prefix-error" className="text-sm text-destructive">
                    {prefixError}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'stockSync.reconcile.prefixHint',
                      'Pushes drifted SKUs matching this prefix off the maintained variant map. Fast, for sale-time fixes.',
                    )}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t(
                  'stockSync.reconcile.fullHint',
                  'Rebuilds the variant map, detects drift across the whole catalogue, then force-pushes corrections. Runs several minutes.',
                )}
              </p>
            )}
          </div>
        </div>

        {/* Primary action */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handlePrimary}
            disabled={submitDisabled}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {triggerReconcile.isPending || running ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {running
                  ? t('stockSync.reconcile.runningButton', 'Reconciling…')
                  : t('stockSync.reconcile.startingButton', 'Starting…')}
              </>
            ) : (
              <>
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                {/* Mode-aware: TARGETED fires immediately, FULL pops the typed-RECONCILE gate. */}
                {mode === 'targeted'
                  ? t('stockSync.reconcile.runButtonTargeted', 'Push targeted SKUs')
                  : t('stockSync.reconcile.runButton', 'Reconcile & push')}
              </>
            )}
          </Button>
        </div>

        {/* Live run status — server-state driven. */}
        {run && (
          <div className="space-y-3">
            {running && (
              <div
                role="status"
                aria-atomic="false"
                className="space-y-2 rounded-lg border border-threadheads-purple/30 bg-threadheads-purple/5 p-3"
              >
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-threadheads-purple motion-reduce:animate-none" aria-hidden="true" />
                  {/*
                    SR announces a STABLE label only — the live "{n} of …" count
                    changes every ~2s (poll tick) and would otherwise spam a new
                    announcement every tick for an ~80-min run. The changing count
                    is rendered aria-hidden; the visible tabular-nums chips below
                    already carry it for sighted users.
                  */}
                  <span className="text-sm font-medium text-foreground">
                    {hasDenominator
                      ? t('stockSync.reconcile.live.checkingStable', 'Reconciling…')
                      : t('stockSync.reconcile.live.starting', 'Starting variant scan… this can take a minute')}
                    {hasDenominator && (
                      <span className="text-muted-foreground tabular-nums" aria-hidden="true">
                        {' '}
                        {t('stockSync.reconcile.live.checkingCount', 'variant {{n}} of …', {
                          n: checked.toLocaleString(),
                        })}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <CountChip label={t('stockSync.reconcile.stat.checked', 'checked')} value={checked} />
                  <CountChip
                    label={t('stockSync.reconcile.stat.drifted', 'drifted')}
                    value={statNum(runStats, 'mismatches')}
                  />
                  <CountChip
                    label={t('stockSync.reconcile.stat.corrected', 'corrected')}
                    value={statNum(runStats, 'corrected')}
                  />
                  <CountChip
                    label={t('stockSync.reconcile.stat.zeroed', 'phantoms zeroed')}
                    value={statNum(runStats, 'zeroed')}
                    amber={statNum(runStats, 'zeroed') > 0}
                  />
                  <CountChip
                    label={t('stockSync.reconcile.stat.linksAdded', 'links added')}
                    value={statNum(runStats, 'linksUpserted')}
                  />
                  <CountChip
                    label={t('stockSync.reconcile.stat.linksArchived', 'links archived')}
                    value={archivedLive}
                    amber={archivedLive >= VARIANT_LINKS_ARCHIVE_THRESHOLD}
                  />
                  <CountChip
                    label={t('stockSync.reconcile.stat.errors', 'errors')}
                    value={statNum(runStats, 'correctionErrors')}
                    tone="destructive"
                  />
                </div>
                {startedAt && (
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" aria-hidden="true" />
                    {t('stockSync.reconcile.live.startedAgo', 'Started {{ago}}', {
                      ago: formatDistanceToNow(new Date(startedAt), { addSuffix: true }),
                    })}
                  </p>
                )}
              </div>
            )}

            {/* P0 — aborted_safeguard is AMBER, never green. */}
            {isAborted && (
              <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium text-amber-800">
                      {t('stockSync.reconcile.aborted.title', 'Reconcile paused for safety')}
                    </p>
                    <p className="text-xs text-amber-700">
                      {t(
                        'stockSync.reconcile.aborted.body',
                        'The variant map changed more than expected, so we stopped before pushing to Shopify. No Shopify stock was changed. Check Sentry/#alerts before retrying.',
                      )}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                      onClick={() => setShowConfirm(true)}
                      disabled={triggerReconcile.isPending}
                    >
                      {t('stockSync.reconcile.aborted.retry', 'Retry full reconcile')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {run.status === 'completed' && (
              <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium text-emerald-900">
                        {t('stockSync.reconcile.complete.title', 'Reconcile complete')}
                      </p>
                      {/* TARGETED linksEmpty → informational amber, not red, not vanishing. */}
                      {linksEmpty && runScope?.skuPrefix && (
                        <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
                          <p className="text-xs text-amber-700">
                            {t(
                              'stockSync.reconcile.complete.linksEmpty',
                              'No linked variants found for {{prefix}}. Nothing was pushed. Check the SKU/prefix, or run a Full reconcile to rebuild the variant map.',
                              { prefix: runScope.skuPrefix },
                            )}
                          </p>
                        </div>
                      )}
                      {/* Completion-summary chips ALWAYS render, even at 0 — zero is
                          meaningful here ("Reconcile complete" but nothing changed). */}
                      <div className="flex flex-wrap gap-2">
                        <CountChip label={t('stockSync.reconcile.stat.checked', 'checked')} value={checked} />
                        <CountChip
                          label={t('stockSync.reconcile.stat.corrected', 'corrected')}
                          value={statNum(runStats, 'corrected')}
                          showZero
                        />
                        <CountChip
                          label={t('stockSync.reconcile.stat.zeroed', 'phantoms zeroed')}
                          value={statNum(runStats, 'zeroed')}
                          amber={statNum(runStats, 'zeroed') > 0}
                        />
                        <CountChip
                          label={t('stockSync.reconcile.stat.linksAdded', 'links added')}
                          value={statNum(runStats, 'linksUpserted')}
                          showZero
                        />
                        <CountChip
                          label={t('stockSync.reconcile.stat.linksArchived', 'links archived')}
                          value={archivedLive}
                          amber={archivedLive >= VARIANT_LINKS_ARCHIVE_THRESHOLD}
                          showZero
                        />
                      </div>
                    </div>
                  </div>
                  {/* Dismiss — the terminal result box is redundant with the toast and
                      the top history row once read. Only the COMPLETED (non-aborted,
                      non-failed) state is dismissible; aborted/failed stay sticky so the
                      operator must act on them. Mirrors the Discover-Now dismiss. */}
                  <button
                    type="button"
                    aria-label={t('stockSync.reconcile.complete.dismiss', 'Dismiss')}
                    onClick={() => setActiveRunId(null)}
                    className="shrink-0 text-emerald-500 hover:text-emerald-700"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

            {run.status === 'failed' && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50/60 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-red-900">
                      {t('stockSync.reconcile.failed.title', 'Reconcile failed')}
                    </p>
                    {run.error && <p className="text-xs text-red-700">{run.error}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Run history */}
        <div className="space-y-2 pt-1">
          <h4 className="text-sm font-medium text-muted-foreground">
            {t('stockSync.reconcile.history.heading', 'Recent reconcile runs')}
          </h4>
          <ReconcileRunHistory />
          {showCardWarning && (
            <div role="status" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <p className="text-xs text-amber-700">
                {latestBreaker
                  ? t(
                      'stockSync.reconcile.cardWarning.breaker',
                      'The most recent reconcile was paused by the safety circuit-breaker. Review the variant map before retrying.',
                    )
                  : t(
                      'stockSync.reconcile.cardWarning.archived',
                      'The most recent reconcile archived {{archived}} variant links, an unusually high number. Confirm this was expected.',
                      { archived: latestArchived.toLocaleString() },
                    )}
              </p>
            </div>
          )}
        </div>
      </CardContent>

      <ReconcileConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirmFull}
        isPending={triggerReconcile.isPending}
        isRetry={isAborted}
      />
    </Card>
  );
}
